// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import HeaderClickable from "@electrowire/App/Components/Header/HeaderClickable";
import MiniMyAccount from "@electrowire/App/Components/MiniMyAccount/MiniMyAccount";
import { TippyPopupStyle } from "@electrowire/App/Components/Tippy/Style";
import TippyPopup from "@electrowire/App/Components/Tippy/TippyPopup";
import { CustomAppState } from "@electrowire/App/Store/Reducers";
import { xIsAuthenticated } from "@electrowire/App/Store/Selectors/GeneralSelectors";
import { EMPTY_VALUE } from "@electrowire/XDL/constants";
import UserCircle from "@electrowire/XDL/Icons/UserCircle";
import XngSignIn from "@gcg/App/Components/XngSignIn/XngSignIn";
import ChevronRight from "@gcg/XDL/Icons/ChevronRight";
import LogIn from "@gcg/XDL/Icons/LogIn";
import MyAccount from "@gcg/XDL/Icons/MyAccount";
import { color } from "@gcg/XDL/Utilities";
import { getCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import logger from "@insite/client-framework/Logger";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest, getIsPunchOutSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadBillTo from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadBillTo";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByNodeId, getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import Clickable, { ClickablePresentationProps, StyledButton } from "@insite/mobius/Clickable";
import Icon, { IconPresentationProps, IconWrapper } from "@insite/mobius/Icon/Icon";
import User from "@insite/mobius/Icons/User";
import Menu from "@insite/mobius/Menu";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    visibilityState = "visibilityState",
    icon = "icon",
    includeAccountMenu = "includeAccountMenu",
    // XNG-Changes
    links = "links",
    isGroup = "isGroup",
}

export interface HeaderMyAccountMappedLink {
    id: string;
    isGroup?: boolean;
    url?: string;
    excludeFromNavigation?: boolean;
    title?: string;
    pageType?: string;
}

export interface CustomLinkModel {
    fields: {
        isGroup: boolean;
        destination: LinkFieldValue;
        title: string;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.visibilityState]: string;
        [fields.icon]: string;
        [fields.includeAccountMenu]: boolean;
        [fields.links]: CustomLinkModel[];
    };
}

const getAllowedLinks = (state: ApplicationState, ownProps: OwnProps): HeaderMyAccountMappedLink[] => {
    return (ownProps.fields.links || [])
        .map((linkItem: CustomLinkModel, index) => {
            const node = getPageLinkByNodeId(state, linkItem?.fields?.destination?.value);
            return {
                id: node?.url || `myAccountItem_${index}`,
                isGroup: linkItem.fields.isGroup,
                url: node?.url || "",
                excludeFromNavigation: node?.excludeFromNavigation,
                title: linkItem.fields.isGroup ? linkItem.fields.title : node?.title,
                pageType: node?.type,
            };
        })
        .filter(
            i =>
                ((!i.excludeFromNavigation || i.isGroup) && i.pageType !== "ChangeCustomerPage") ||
                (i.pageType === "ChangeCustomerPage" && state.context.session.displayChangeCustomerLink),
        );
};

const mapStateToProps = (state: CustomAppState, ownProps: OwnProps) => ({
    isSignInPage: getCurrentPage(state).type === "SignInPage",
    userName: state.context.session.userName,
    myAccountPageLink: getPageLinkByPageType(state, "MyAccountPage"),
    signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
    currentUserIsGuest: getCurrentUserIsGuest(state),
    isPunchOutSession: getIsPunchOutSession(state),
    currentLocation: getLocation(state),
    displayChangeCustomerLink: state.context.session?.displayChangeCustomerLink || false,
    // XNG-Changes
    isSticky: state.xHeader.isSticky,
    session: state.context.session,
    selectedBillToId: state.context.session.billToId,
    billToState: getBillToState(state, state.context.session.billToId),
    links: getAllowedLinks(state, ownProps),
    changeCustomerUrl: getPageLinkByPageType(state, "ChangeCustomerPage")?.url,
    isAuthenticated: xIsAuthenticated(state),
    firstName: state.context.session.firstName,
    isTabletScreen: state.xCommon.isTabletScreen,
});

const mapDispatchToProps = {
    signOut,
    cancelPunchOut,
    // XNG-Changes
    loadBillTo,
};

export interface HeaderSignInStyles {
    signOutWrapper?: InjectableCss;
    titleClickable?: ClickablePresentationProps;
    titleIcon?: IconPresentationProps;
    titleTypography?: TypographyPresentationProps;
    signOutClickable?: ClickablePresentationProps;
    // XNG-Changes
    tippyStyles?: TippyPopupStyle;
}

export const headerSignInStyles: HeaderSignInStyles = {
    signOutWrapper: {
        css: css`
            display: flex;
        `,
    },
    tippyStyles: {
        main: {
            css: css`
                &.tippy-box > .tippy-content {
                    padding: 0;
                }
            `,
        },
        trigger: {
            css: css`
                & > ${StyledButton} > ${TypographyStyle}, & > ${StyledButton} > ${IconWrapper} {
                    transition: transform ease-in-out 250ms;
                }

                &[aria-expanded="true"] {
                    & > ${StyledButton} > ${IconWrapper} {
                        color: ${color("common.ui8")};
                    }
                    & > ${StyledButton} > ${TypographyStyle} {
                        color: ${color("common.ui8")};
                    }
                }
            `,
        },
    },
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

let icon: React.ComponentType<IconPresentationProps>;
export const HeaderSignIn = ({
    userName,
    isSignInPage,
    currentUserIsGuest,
    signOut,
    cancelPunchOut,
    fields,
    history,
    myAccountPageLink,
    signInUrl,
    isPunchOutSession,
    currentLocation,
    displayChangeCustomerLink,
    links,
    isAuthenticated,
    firstName,
    isTabletScreen,
}: Props) => {
    const showIcon = fields.visibilityState === "both" || fields.visibilityState === "icon";
    const showLabel = fields.visibilityState === "both" || fields.visibilityState === "label";
    const [isPopupShown, setIsPopupShown] = useState(false);

    if (fields.icon === "User") {
        icon = UserCircle;
    }

    const styles = useMergeStyles("headerSignIn", headerSignInStyles);

    const currentMode = getCookie(NavigationModeCookieName);
    let menuItems = myAccountPageLink?.children;
    if (!currentMode || currentMode === "Storefront") {
        menuItems = menuItems?.filter(pageLink => pageLink.type !== "VmiUsersPage");
    }
    const onSignInHandler = (e: React.MouseEvent) => {
        if (isAuthenticated) {
            history.push(`/MyAccount`);
            return;
        }

        if ((!userName || currentUserIsGuest) && !isSignInPage) {
            if (!signInUrl) {
                logger.warn("No url was found for SignInPage, defaulting to /SignIn");
                history.push("/MyAccount/SignIn");
            } else {
                const returnUrl = currentLocation.pathname + currentLocation.search;
                if (returnUrl === "/") {
                    history.push(`${signInUrl}`);
                } else {
                    history.push(`${signInUrl}?returnUrl=${encodeURIComponent(returnUrl)}&clientRedirect=true`);
                }
            }
            e.preventDefault();
        }
    };

    const onSignOutHandler = () => {
        isPunchOutSession ? cancelPunchOut() : signOut();
    };

    let signInStatusText = translate("Sign In / Register");
    if (userName && !currentUserIsGuest) {
        signInStatusText = userName;
    }

    const headerClickableElement = (
        <HeaderClickable
            isPopupShown={isPopupShown}
            onClick={onSignInHandler}
            iconProps={{ 
                src: UserCircle,
                id: "myAccount",
            }}
            titleProps={{ 
                id: "myAccount" 
            }}
            data-test-selector="header_signIn"
            aria-label="SignIn"
        >
            {isAuthenticated ? translate(firstName || userName || EMPTY_VALUE) : translate("Register / Log In")}
        </HeaderClickable>
    );

    return (
        <StyledWrapper {...styles.signOutWrapper}>
            {isTabletScreen ? (
                headerClickableElement
            ) : (
                <TippyPopup
                    content={
                        isAuthenticated ? (
                            <MiniMyAccount links={links} onSignOutHandler={onSignOutHandler} />
                        ) : (
                            <XngSignIn />
                        )
                    }
                    arrow={true}
                    offset={[0, 14]}
                    maxWidth={400}
                    placement="bottom-end"
                    type="menu"
                    showCloseButton={false}
                    extendedStyles={styles.tippyStyles}
                >
                    {headerClickableElement}
                </TippyPopup>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(HeaderSignIn)),
    definition: {
        displayName: "Header Sign In / MyAccount Links",
        icon: "arrow-right-to-bracket",
        fieldDefinitions: [
            {
                name: fields.visibilityState,
                displayName: "Settings",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Both",
                        value: "both",
                    },
                    {
                        displayName: "Show Label",
                        value: "label",
                    },
                    {
                        displayName: "Show Icon",
                        value: "icon",
                    },
                ],
                defaultValue: "both",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.icon,
                displayName: "SignIn Icon",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "User",
                        value: "User",
                    },
                ],
                defaultValue: "User",
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.includeAccountMenu,
                displayName: "Include My Account Menu",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.links,
                editorTemplate: "ListField",
                isVisible: item => item.fields.includeAccountMenu,
                getDisplay: (item: HasFields, state) => {
                    const { type, value } = item.fields.destination;

                    if (item.fields[fields.isGroup]) {
                        return `Group Name: ${item.fields.title}`;
                    }

                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return link?.title;
                    }

                    if (type === "Category") {
                        return value;
                    }
                    if (type === "Url") {
                        return value;
                    }

                    return value;
                },
                defaultValue: [],
                fieldType: "General",
                sortOrder: 4,
                fieldDefinitions: [
                    {
                        name: fields.isGroup,
                        displayName: "Is group",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    {
                        name: "destination",
                        editorTemplate: "CustomLinkField" as any,
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isVisible: item => !item.fields[fields.isGroup],
                    },
                    {
                        name: "title",
                        displayName: "Groupe Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isVisible: item => item.fields[fields.isGroup] === true,
                    },
                ],
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
